'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'sonner'
import {
  Loader2Icon,
  Mail,
  Lock,
  User,
  CheckCircle2,
  Trash2,
  AlertTriangle,
} from 'lucide-react'
import { useTranslations } from 'next-intl'

import { useCurrentUser } from '@/hooks/use-current-user'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { popModal, pushModal } from '@/components/modals'
import { nanoid } from 'nanoid'
import { cn } from '@/lib/utils'

export default function SettingModal() {
  const { data: user, mutate } = useCurrentUser()
  const [username, setUsername] = useState(user?.username || '')
  const [newEmail, setNewEmail] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isChangingEmail, setIsChangingEmail] = useState(false)
  const [emailVerificationSent, setEmailVerificationSent] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const verificationInputRef = useRef<HTMLInputElement>(null)
  const t = useTranslations('modals.setting')

  // Sync username when user data updates
  useEffect(() => {
    if (user?.username !== undefined) {
      setUsername(user.username || '')
    }
  }, [user?.username])

  // Auto-focus verification code input when it appears
  useEffect(() => {
    if (emailVerificationSent && verificationInputRef.current) {
      verificationInputRef.current.focus()
    }
  }, [emailVerificationSent])

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCountdown])

  const handleSave = async () => {
    if (!username) return

    setIsLoading(true)
    try {
      const res = await fetch('/api/me/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({ username }),
      })
      const data = await res.json()

      if (!data.success) {
        if (data.error === 'usernameExists') {
          toast.error(t('errors.username_exists'))
        } else if (data.error === 'usernameInvalid') {
          toast.error(t('errors.username_invalid'))
        } else if (data.error === 'usernameReserved') {
          toast.error(t('errors.username_reserved'))
        } else {
          toast.error(t('errors.save_failed'))
        }
        return
      }

      await mutate()
      toast.success(t('success'))
      popModal()
    } catch (error) {
      console.error('Save settings error:', error)
      toast.error(t('errors.save_failed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleRequestEmailChange = async () => {
    if (!newEmail || newEmail === user?.email) {
      toast.error(t('errors.email_same'))
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(newEmail)) {
      toast.error(t('errors.email_invalid'))
      return
    }

    setIsChangingEmail(true)
    try {
      const res = await fetch('/api/me/change-email/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({ newEmail }),
      })
      const data = await res.json()

      if (!data.success) {
        if (data.error === 'emailExists') {
          toast.error(t('errors.email_exists'))
        } else if (data.error === 'emailInvalid') {
          toast.error(t('errors.email_invalid'))
        } else {
          toast.error(t('errors.email_change_failed'))
        }
        return
      }

      setEmailVerificationSent(true)
      setResendCountdown(60) // 60 second countdown
      toast.success(t('email_verification_sent'))
    } catch (error) {
      console.error('Request email change error:', error)
      toast.error(t('errors.email_change_failed'))
    } finally {
      setIsChangingEmail(false)
    }
  }

  const handleResendCode = async () => {
    if (resendCountdown > 0) return
    await handleRequestEmailChange()
  }

  const handleVerifyEmailChange = useCallback(async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t('errors.verification_code_invalid'))
      return
    }

    setIsChangingEmail(true)
    try {
      const res = await fetch('/api/me/change-email/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({ newEmail, verificationCode }),
      })
      const data = await res.json()

      if (!data.success) {
        if (data.error === 'invalidCode') {
          toast.error(t('errors.verification_code_invalid'))
        } else if (data.error === 'expiredCode') {
          toast.error(t('errors.verification_code_expired'))
        } else {
          toast.error(t('errors.email_change_failed'))
        }
        return
      }

      await mutate()
      toast.success(t('email_change_success'))
      setNewEmail('')
      setVerificationCode('')
      setEmailVerificationSent(false)
      popModal()
    } catch (error) {
      console.error('Verify email change error:', error)
      toast.error(t('errors.email_change_failed'))
    } finally {
      setIsChangingEmail(false)
    }
  }, [verificationCode, newEmail, t, mutate])

  // Handle Enter key to submit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'Enter' &&
        emailVerificationSent &&
        verificationCode.length === 6 &&
        !isChangingEmail
      ) {
        e.preventDefault()
        handleVerifyEmailChange()
      }
      if (e.key === 'Escape' && emailVerificationSent) {
        e.preventDefault()
        setEmailVerificationSent(false)
        setVerificationCode('')
        setNewEmail('')
        setResendCountdown(0)
      }
    }
    if (emailVerificationSent) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    emailVerificationSent,
    verificationCode,
    isChangingEmail,
    handleVerifyEmailChange,
  ])

  return (
    <DialogContent
      className={cn(
        'sm:max-w-[520px] max-w-[95vw]',
        'bg-[#1a1a1a] border border-gold/30',
        'shadow-[0_0_30px_rgba(255,215,0,0.2)]',
        'backdrop-blur-xl',
        'p-0 gap-0 overflow-hidden',
      )}
      overlayClassName="bg-black/80 backdrop-blur-sm"
    >
      {/* Header with Gold Accent */}
      <DialogHeader
        className={cn(
          'px-6 pt-6 pb-4',
          'border-b border-gold/20',
          'bg-gradient-to-b from-gold/5 to-transparent',
        )}
      >
        <DialogTitle
          className={cn(
            'text-2xl font-bold text-white',
            'flex items-center gap-2',
          )}
        >
          <div className="w-1 h-8 bg-gold rounded-full" />
          {t('title')}
        </DialogTitle>
      </DialogHeader>

      {/* Content */}
      <div className="px-6 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
        {/* Username Section */}
        <div className="space-y-4">
          <Label
            htmlFor="username"
            className="text-white/90 font-medium flex items-center gap-2"
          >
            <User className="w-4 h-4 text-gold" />
            {t('username')}
          </Label>
          <div className="flex gap-3">
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('username_placeholder')}
              maxLength={20}
              className={cn(
                'flex-1 bg-[#0f0f0f] border-gold/30 text-white',
                'placeholder:text-white/40',
                'focus:border-gold focus:ring-gold/20',
                'transition-all duration-200',
              )}
            />
            <Button
              onClick={handleSave}
              disabled={!username || isLoading || username === user?.username}
              className={cn(
                'h-11 px-6 shrink-0',
                'bg-gold-gradient text-black font-semibold',
                'shadow-[0_0_15px_-5px_rgba(255,215,0,0.3)]',
                'hover:shadow-[0_0_20px_-5px_rgba(255,215,0,0.5)]',
                'hover:scale-[1.02] active:scale-[0.98]',
                'transition-all duration-200',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
              )}
            >
              {isLoading ? (
                <>
                  <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
                  {t('saving')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {t('save_changes')}
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-white/60 flex items-center gap-1.5">
            <Lock className="w-3 h-3 text-gold/60" />
            {t('subdomain_hint', {
              username: username || user?._id?.toString() || 'username',
            })}
          </p>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />

        {/* Email Section */}
        <div className="space-y-4">
          <Label
            htmlFor="email"
            className="text-white/90 font-medium flex items-center gap-2"
          >
            <Mail className="w-4 h-4 text-gold" />
            {t('email')}
          </Label>

          {/* Current Email Display */}
          {user?.email && (
            <div
              className={cn(
                'p-3 rounded-lg',
                'bg-[#0f0f0f] border border-gold/20',
                'flex items-center gap-2',
              )}
            >
              <CheckCircle2 className="w-4 h-4 text-gold shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white/50 mb-0.5">
                  {t('current_email', { email: user.email }).split(':')[0]}
                </p>
                <p className="text-sm text-white font-medium truncate">
                  {user.email}
                </p>
              </div>
            </div>
          )}

          {!emailVerificationSent ? (
            <div className="space-y-3">
              <Input
                id="email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('email_placeholder')}
                disabled={isChangingEmail}
                className={cn(
                  'bg-[#0f0f0f] border-gold/30 text-white',
                  'placeholder:text-white/40',
                  'focus:border-gold focus:ring-gold/20',
                  'transition-all duration-200',
                )}
              />
              <Button
                onClick={handleRequestEmailChange}
                disabled={
                  !newEmail || isChangingEmail || newEmail === user?.email
                }
                className={cn(
                  'w-full h-11',
                  'bg-gold-gradient text-black font-bold',
                  'shadow-[0_0_20px_-5px_rgba(255,215,0,0.4)]',
                  'hover:shadow-[0_0_30px_-5px_rgba(255,215,0,0.6)]',
                  'hover:scale-[1.02] active:scale-[0.98]',
                  'transition-all duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
                )}
              >
                {isChangingEmail ? (
                  <>
                    <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
                    {t('sending')}
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    {t('change_email')}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Success Message */}
              <div
                className={cn(
                  'p-4 rounded-lg',
                  'bg-gold/10 border border-gold/30',
                  'flex items-start gap-3',
                )}
              >
                <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gold mb-1">
                    {t('verification_code_sent', { email: newEmail })}
                  </p>
                  <p className="text-xs text-white/60 mt-2">
                    {t('verification_code_expires')}
                  </p>
                </div>
              </div>

              {/* Verification Code Input */}
              <div className="space-y-2">
                <Label className="text-white/80 text-sm">
                  {t('verification_code_placeholder')}
                </Label>
                <Input
                  ref={verificationInputRef}
                  type="text"
                  inputMode="numeric"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setVerificationCode(value)
                  }}
                  onPaste={(e) => {
                    e.preventDefault()
                    const pasted = e.clipboardData
                      .getData('text')
                      .replace(/\D/g, '')
                      .slice(0, 6)
                    setVerificationCode(pasted)
                  }}
                  placeholder={t('verification_code_placeholder')}
                  maxLength={6}
                  disabled={isChangingEmail}
                  className={cn(
                    'text-center text-2xl tracking-[0.5em] font-mono font-bold',
                    'bg-[#0f0f0f] border-2 border-gold/40 text-gold',
                    'focus:border-gold focus:ring-4 focus:ring-gold/20',
                    'transition-all duration-200',
                    'h-16',
                  )}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleVerifyEmailChange}
                  disabled={
                    !verificationCode ||
                    verificationCode.length !== 6 ||
                    isChangingEmail
                  }
                  className={cn(
                    'flex-1 h-11',
                    'bg-gold-gradient text-black font-bold',
                    'shadow-[0_0_20px_-5px_rgba(255,215,0,0.4)]',
                    'hover:shadow-[0_0_30px_-5px_rgba(255,215,0,0.6)]',
                    'hover:scale-[1.02] active:scale-[0.98]',
                    'transition-all duration-200',
                    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
                  )}
                >
                  {isChangingEmail ? (
                    <>
                      <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
                      {t('verifying')}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {t('verify_and_change')}
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => {
                    setEmailVerificationSent(false)
                    setVerificationCode('')
                    setNewEmail('')
                    setResendCountdown(0)
                  }}
                  variant="outline"
                  disabled={isChangingEmail}
                  className={cn(
                    'h-11 px-4',
                    'border-gold/30 text-white/80',
                    'hover:bg-gold/10 hover:border-gold/50 hover:text-white',
                    'transition-all duration-200',
                  )}
                >
                  {t('cancel')}
                </Button>
              </div>

              {/* Resend Code */}
              <div className="flex items-center justify-center gap-2 pt-2">
                <span className="text-xs text-white/60">
                  {t('didnt_receive_code')}
                </span>
                <Button
                  variant="link"
                  onClick={handleResendCode}
                  disabled={resendCountdown > 0 || isChangingEmail}
                  className={cn(
                    'h-auto p-0 text-xs font-medium',
                    'text-gold hover:text-gold/80',
                    'disabled:text-white/40',
                    'transition-colors',
                  )}
                >
                  {resendCountdown > 0
                    ? t('resend_in', { seconds: resendCountdown })
                    : t('resend_code')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-red-500/20 to-transparent" />

        {/* Danger Zone - Delete Account */}
        <div
          className={cn(
            'space-y-4 p-4 rounded-lg',
            'bg-red-500/5 border border-red-500/20',
          )}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-white mb-1">
                {t('delete_account_section_title', { default: 'Danger Zone' })}
              </h3>
              <p className="text-sm text-white/60 mb-4">
                {t('delete_account_section_description', {
                  default:
                    'Once you delete your account, there is no going back. Please be certain.',
                })}
              </p>
              <Button
                onClick={() => {
                  popModal()
                  pushModal('DeleteAccountConfirmModal')
                }}
                variant="outline"
                className={cn(
                  'h-10 px-4',
                  'border-red-500/50 text-red-400',
                  'hover:bg-red-500/10 hover:border-red-500/70 hover:text-red-300',
                  'transition-all duration-200',
                )}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('delete_account', { default: 'Delete Account' })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  )
}
